//Copyright Open Trade Technology Ltd. 2023. All rights reserved.

import { Box, Button, Grid, InputLabel, TextField, Typography } from '@mui/material'
import React, { ReactElement } from 'react'

import { theme } from '@react/components/theme'

import { useOtChainTimestamp, useOtDispatch, useOtSelector } from '@react/redux/store'
import { EthAddr } from '@shared/SchemaTypes'
import {
  assetsAndSharesToExchangeRate,
  dayNumberToDateString,
  erc20WeiToCommaDecString,
  exchangeRateDecimalStringToExchangeRate,
  exchangeRateToDecimalString,
  toUTCDateString,
} from '@shared/conversions'
import { PoolDynamicOverviewCTO } from '@shared/PoolDynamicCTO'
import { TableHeaderItem } from '@react/components/TableHeaderItem'
import { EXCHANGE_RATE_ONE } from '@shared/Enums'
import { dynamicExchangeRateSetTx } from '@react/signedTxs/dynamicSetDynamicExchangeRate'
import { logOT } from '@shared/loggingShared'

export default function DynamicSetDynamicExchangeRateForm(props: { poolAddr: EthAddr }): ReactElement {
  const { poolAddr } = props

  const poolCTO = useOtSelector((state) => state.poolState.poolOverviewCTOs[poolAddr] as PoolDynamicOverviewCTO)
  const [exchangeRate, setExchangeRate] = React.useState<string>(poolCTO.exchangeRate)
  const [exchangeRateDecimalString, setExchangeRateDecimalString] = React.useState<string>(
    exchangeRateToDecimalString(poolCTO.exchangeRate)
  )
  const [assetValue, setAssetValue] = React.useState<string>(erc20WeiToCommaDecString(poolCTO.totalAssets))

  const chainTimestamp = useOtChainTimestamp()
  const dispatch = useOtDispatch()

  const [errorAmount, _setErrorAmount] = React.useState<string | undefined>(undefined)

  const [isSubmitted, setIsSubmitted] = React.useState(false)

  const sx = {
    backgroundColor: theme.palette.fg.main,
    color: '#FFFFFF',
    padding: '11px 16px 11px 16px',
    marginLeft: '8px',
    borderRadius: '6px',
    marginTop: 2,
  }

  const setExchangerateAction = (): void => {
    setIsSubmitted(true)
    dispatch(
      dynamicExchangeRateSetTx({
        poolAddr,
        exchangeRate,
      })
    )
    const event = new Event('modalClose')
    window.dispatchEvent(event)
  }
  const disableButton = false

  const newAssetsBI = (BigInt(exchangeRate) * BigInt(poolCTO.totalShares)) / EXCHANGE_RATE_ONE

  return (
    <Grid container sx={{ marginTop: 2 }}>
      <Grid container spacing={2}>
        <Grid item xs={3}>
          <TableHeaderItem>Assets</TableHeaderItem>
        </Grid>
        <Grid item xs={3}>
          <TableHeaderItem>Shares</TableHeaderItem>
        </Grid>
        <Grid item xs={2}>
          <TableHeaderItem>Date</TableHeaderItem>
        </Grid>
        <Grid item xs={4}>
          <TableHeaderItem>Exchange Rate</TableHeaderItem>
        </Grid>
      </Grid>

      <Grid container sx={{ marginBottom: 4 }}>
        <Grid item xs={3}>
          <Box sx={{ width: '75%' }}>{erc20WeiToCommaDecString(poolCTO.totalAssets)} </Box>
        </Grid>
        <Grid item xs={3}>
          <Typography sx={{ marginLeft: 1 }} variant="bodyBold">
            {erc20WeiToCommaDecString(poolCTO.totalShares)}
          </Typography>
        </Grid>
        <Grid item xs={2}>
          <Typography sx={{ marginLeft: 1 }} variant="bodyBold">
            {dayNumberToDateString(poolCTO.exchangeRateSetDay)}
          </Typography>
        </Grid>
        <Grid item xs={4}>
          <Typography sx={{ marginLeft: 1 }} variant="bodyBold">
            {exchangeRateToDecimalString(poolCTO.exchangeRate)}
          </Typography>
        </Grid>
      </Grid>
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <TableHeaderItem>Updated values</TableHeaderItem>
        </Grid>
      </Grid>
      <Grid container sx={{ marginBottom: 4 }}>
        <Grid item xs={3}>
          <Box sx={{ width: '75%' }}>{erc20WeiToCommaDecString(newAssetsBI)} </Box>
        </Grid>
        <Grid item xs={3}>
          <Typography sx={{ marginLeft: 1 }} variant="bodyBold">
            {erc20WeiToCommaDecString(poolCTO.totalShares)}
          </Typography>
        </Grid>
        <Grid item xs={2}>
          <Typography sx={{ marginLeft: 1 }} variant="bodyBold">
            {toUTCDateString(chainTimestamp)}
          </Typography>
        </Grid>
        <Grid item xs={4}>
          <Typography sx={{ marginLeft: 1 }} variant="bodyBold">
            {exchangeRateDecimalString}
          </Typography>
        </Grid>
      </Grid>
      <Grid item xs={12}>
        <InputLabel htmlFor="assetValue">Updated Asset Value</InputLabel>
        <TextField
          id="assetValue"
          inputProps={{
            'data-testid': 'assetValue',
          }}
          sx={{ width: '300px' }}
          variant="outlined"
          placeholder="Enter Exchange Rate (Decimal Form)"
          value={assetValue}
          type={'tel'}
          onChange={(event: React.ChangeEvent<HTMLInputElement>): void => {
            setAssetValue(event.target.value)
            const sanitizedValue = event.target.value.replace(/,/g, '')
            const assetvalueNumber = Number(sanitizedValue)

            logOT.info('assetvalueNumber ', assetvalueNumber, event.target.value)
            const er = assetsAndSharesToExchangeRate(assetvalueNumber, poolCTO.totalShares)
            setExchangeRateDecimalString(exchangeRateToDecimalString(er))
            setExchangeRate(er.toString())
          }}
          onFocus={() => {
            logOT.info('onFocus')
          }}
          onBlur={() => {
            logOT.info('onBlur')
          }}
          error={!!errorAmount}
          helperText={errorAmount}
        />
      </Grid>
      <Grid item xs={12}>
        <InputLabel htmlFor="exchangeRate">ExchangeRate</InputLabel>
        <TextField
          id="exchangeRate"
          inputProps={{
            'data-testid': 'exchangeRate',
          }}
          sx={{ width: '300px' }}
          variant="outlined"
          placeholder="Enter Exchange Rate (Decimal Form)"
          value={exchangeRateDecimalString}
          type={'tel'}
          onChange={(event: React.ChangeEvent<HTMLInputElement>): void => {
            setExchangeRateDecimalString(event.target.value)
            setExchangeRate(exchangeRateDecimalStringToExchangeRate(event.target.value).toString())
            const assets =
              (exchangeRateDecimalStringToExchangeRate(event.target.value) * BigInt(poolCTO.totalShares)) /
              EXCHANGE_RATE_ONE

            setAssetValue(erc20WeiToCommaDecString(assets))
          }}
          onFocus={() => {
            logOT.info('onFocus')
          }}
          onBlur={() => {
            logOT.info('onBlur')
          }}
          error={!!errorAmount}
          helperText={errorAmount}
        />
      </Grid>
      <Grid item xs={12} sm={12}>
        <Button
          disabled={isSubmitted || disableButton}
          sx={sx}
          onClick={() => {
            setExchangerateAction()
          }}
          variant={'contained'}
        >
          <Typography variant={'buttonBold'}>Set Exchange rate</Typography>
        </Button>
      </Grid>
      {disableButton && (
        <Typography variant="body" align="left" sx={{ marginBottom: 2 }}>
          Disabled
        </Typography>
      )}
    </Grid>
  )
}
